// React Imports
import { useState, useEffect, useRef } from "react";

// 3rd Party Imports
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { Divider } from "primereact/divider";
import DOMPurify from "dompurify";

// SJ-UI Toolkit Imports
import { getAllUserNotes, updateNote, downloadAllUserNotes } from "sj-ui-toolkit/src/api";
import { QuillEditor } from "sj-ui-toolkit/src/components/QuillEditor/QuillEditor";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";

type NoteCollections = Awaited<ReturnType<typeof getAllUserNotes>>;
type NoteItem = NoteCollections[number]["notes"][number];

export function UserNotesTab() {
  useBreadcrumb({ label: "Notes", route: "/profile/notes", display: "page" });
  const [isLoading, setIsLoading] = useState(true);
  const [noteCollections, setNoteCollections] = useState<NoteCollections>([]);
  const { navigateTo } = useSmartNavigation();
  const downloadMenuRef = useRef<Menu>(null);

  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [areNotesDownloading, setAreNotesDownloading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const downloadOptions = [
    {
      label: "Download as CSV",
      icon: "pi pi-file-excel",
      command: () => handleDownloadNotes("csv"),
    },
    { label: "Download as TXT", icon: "pi pi-file", command: () => handleDownloadNotes("txt") },
  ];

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const data = await getAllUserNotes();
        setNoteCollections(data);
      } catch (error) {
        console.error("Error fetching user notes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotes();
  }, []);

  const showEmptyState = !isLoading && noteCollections.length === 0;

  const handleDownloadNotes = async (format: "csv" | "txt") => {
    setAreNotesDownloading(true);
    try {
      await downloadAllUserNotes(format);
    } catch (error) {
      console.error("Error downloading notes:", error);
    } finally {
      setAreNotesDownloading(false);
    }
  };

  const handleEditClick = (note: NoteItem) => {
    setStatusMessage("");
    setEditingNoteId(note.id);
    setEditingText(note.text);
  };

  const handleCancel = () => {
    setEditingNoteId(null);
    setEditingText("");
  };

  const handleSave = async () => {
    if (editingNoteId === null) return;
    setIsSaving(true);
    try {
      await updateNote({ noteId: String(editingNoteId), noteText: editingText });

      // Update the local state with the new text
      setNoteCollections(prevCollections =>
        prevCollections.map(collection => ({
          ...collection,
          notes: collection.notes.map(note =>
            note.id === editingNoteId ? { ...note, text: editingText } : note
          ),
        }))
      );

      setEditingNoteId(null);
      setEditingText("");
      setStatusMessage("Note saved");
    } catch (error) {
      console.error("Error updating note:", error);
      setStatusMessage("Error saving note");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="w-2/3 min-w-4xl px-[40px]"
        aria-busy="true"
        aria-label="Loading notes"
        data-testid="notes-tab-skeleton"
      >
        <div className="flex flex-col gap-3">
          {Array(10)
            .fill(null)
            .map((_, i) => (
              <Skeleton key={i} width="100%" height="1.5rem" className="mb-3" />
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-2/3 min-w-4xl px-[40px]" data-testid="notes-tab">
      <h2 className="atlas-h5 flex justify-between items-center mb-4">
        Notes
        {!showEmptyState && (
          <>
            <Menu
              model={downloadOptions}
              popup
              ref={downloadMenuRef}
              id="download-menu"
              onShow={() => setIsMenuOpen(true)}
              onHide={() => setIsMenuOpen(false)}
              data-testid="notes-download-menu"
            />
            <Button
              aria-label="Download Notes"
              aria-controls="download-menu"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              loading={areNotesDownloading}
              disabled={areNotesDownloading}
              icon="pi pi-download"
              className="p-button-text"
              onClick={e => downloadMenuRef.current?.toggle(e)}
              data-testid="notes-download-button"
            />
          </>
        )}
      </h2>
      <section aria-label="User notes">
        {showEmptyState ? (
          <>
            <p className="atlas-h1">
              You don’t have any <br />
              notes yet.
            </p>
            <p className="body-sm text-muted">You can add notes in each lesson.</p>
            <Button
              className="mt-5"
              outlined
              label="View Catalog"
              onClick={() => navigateTo(INTERNAL_ROUTES.Learn.routes.catalog.route)}
              data-testid="notes-view-catalog-button"
            />
          </>
        ) : (
          <Accordion multiple aria-label="Note collections">
            {noteCollections.map(collection => (
              <AccordionTab
                key={collection.id}
                header={collection.name}
                pt={{
                  headerAction: {
                    className: "flex-row-reverse",
                    href: null,
                    "data-testid": `note-collection-${collection.id}`,
                  },
                  headerTitle: { className: "text-color" },
                }}
              >
                {collection.notes.map((note, index) => {
                  const isEditing = editingNoteId === note.id;

                  return (
                    <div key={note.id}>
                      {isEditing ? (
                        <>
                          <QuillEditor
                            value={editingText}
                            onChange={setEditingText}
                            ariaLabel="Edit note"
                            testId={`note-${note.id}-editor`}
                          />
                          <div className="flex justify-end gap-3 mt-3">
                            <Button
                              severity="secondary"
                              label="Cancel"
                              onClick={handleCancel}
                              disabled={isSaving}
                              data-testid={`note-${note.id}-cancel-button`}
                            />
                            <Button
                              label="Save"
                              onClick={handleSave}
                              loading={isSaving}
                              data-testid={`note-${note.id}-save-button`}
                            />
                          </div>
                          <Divider />
                        </>
                      ) : (
                        <>
                          <article
                            className="ql-editor"
                            aria-label={`${collection.name} - Note ${index + 1}`}
                            data-testid={`note-${note.id}`}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(note.text, {
                                USE_PROFILES: { html: true },
                              }),
                            }}
                          />
                          <div className="text-right">
                            <Button
                              className="p-button-text"
                              label="Edit note"
                              onClick={() => handleEditClick(note)}
                              data-testid={`note-${note.id}-edit-button`}
                            />
                          </div>
                          <Divider />
                        </>
                      )}
                    </div>
                  );
                })}
              </AccordionTab>
            ))}
          </Accordion>
        )}
      </section>
      <div aria-live="polite" role="status" aria-atomic="true" className="sr-only">
        {statusMessage}
      </div>
    </div>
  );
}
